// Test one template against a real visit, without writing anything: C's preview
// route says what the practice's active templates would make of the visit, and
// whether THIS template (on or off) would match it and why not.
//
// The visit picker lists only what C's routes already expose for this practice:
// recent attended visits without a charge (GET /charges/pending, last 30 days) and
// the drafts waiting for a person (GET /charges/needs-person).

import React, { useEffect, useState } from "react";
import { formatPatientName } from "../../utils/formatName";
import { fetchNeedsPerson, fetchPending, lineLabel, previewVisit } from "./api";
import "./Charges.css";

const OUTCOME_LABELS = {
  draft: "A draft with lines",
  needs_person: "Needs a person",
  do_not_bill: "Not billed",
};

export default function TemplateTest({ practiceId, template, onClose }) {
  const [visits, setVisits] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [appointmentId, setAppointmentId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchPending(practiceId), fetchNeedsPerson(practiceId)])
      .then(([pending, needs]) => {
        if (cancelled) return;
        const seen = new Set();
        const rows = [];
        for (const row of [
          ...needs.drafts,
          ...pending.visits,
          ...pending.notBillable,
        ]) {
          if (row.appointmentId == null || seen.has(row.appointmentId))
            continue;
          seen.add(row.appointmentId);
          rows.push(row);
        }
        rows.sort((a, b) =>
          (b.dateOfService || "").localeCompare(a.dateOfService || ""),
        );
        setVisits(rows);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const run = async () => {
    if (!appointmentId || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await previewVisit(practiceId, Number(appointmentId), template.id),
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chg-modal-backdrop" onClick={onClose}>
      <div
        className="chg-modal chg-modal-wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chg-test-title"
      >
        <div className="chg-modal-header">
          <h2 id="chg-test-title">Test {template.name} against a visit</h2>
          <button
            className="chg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="chg-modal-body">
          <p className="chg-muted">
            Nothing is written. No claim is created or changed.
          </p>

          {loadError && (
            <div className="chg-message error" role="alert">
              {loadError}
            </div>
          )}
          {!loadError && !visits && (
            <p className="chg-muted">Loading visits…</p>
          )}
          {visits && visits.length === 0 && (
            <p className="chg-muted">
              No recent visit without a charge and no draft waiting, so there is
              nothing to test against yet.
            </p>
          )}
          {visits && visits.length > 0 && (
            <div className="chg-test-pick">
              <label className="chg-field">
                <span>Visit</span>
                <select
                  value={appointmentId}
                  onChange={(e) => {
                    setAppointmentId(e.target.value);
                    setResult(null);
                  }}
                >
                  <option value="">Choose a visit…</option>
                  {visits.map((v) => (
                    <option key={v.appointmentId} value={v.appointmentId}>
                      {v.dateOfService || "—"} ·{" "}
                      {formatPatientName(v.patientName)} ·{" "}
                      {v.visitKind || "no visit type"}
                      {v.sourceColor ? ` · ${v.sourceColor}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="btn btn-primary btn-sm"
                onClick={run}
                disabled={!appointmentId || busy}
              >
                {busy ? "Testing…" : "Test"}
              </button>
            </div>
          )}

          {error && (
            <div className="chg-message error" role="alert">
              {error}
            </div>
          )}

          {result && (
            <div className="chg-test-result" data-testid="template-test-result">
              {result.tested && (
                <div
                  className={`chg-message ${result.tested.holds ? "success" : "warn"}`}
                >
                  <div>
                    <strong>
                      {template.name}{" "}
                      {result.tested.holds
                        ? "matches this visit."
                        : "does not match this visit."}
                    </strong>
                    {!result.tested.holds &&
                      result.tested.reasons.length > 0 && (
                        <ul>
                          {result.tested.reasons.map((r, i) => (
                            <li key={i}>
                              {typeof r === "string" ? r : JSON.stringify(r)}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              )}
              <dl className="chg-facts-list">
                <dt>With the templates that are on</dt>
                <dd>
                  {OUTCOME_LABELS[result.outcome] || result.outcome || "—"}
                  {result.template ? ` — ${result.template.name}` : ""}
                  {result.reason ? ` (${result.reason})` : ""}
                </dd>
                {result.lines.length > 0 && (
                  <>
                    <dt>Lines</dt>
                    <dd>
                      {result.lines.map((l, i) => (
                        <div key={i}>{lineLabel(l)}</div>
                      ))}
                    </dd>
                  </>
                )}
                {result.billTo && (
                  <>
                    <dt>Bill to</dt>
                    <dd>{result.billTo}</dd>
                  </>
                )}
                {result.payerOverridesApplied.length > 0 && (
                  <>
                    <dt>Payer rules applied</dt>
                    <dd>
                      {result.payerOverridesApplied.map(String).join(", ")}
                    </dd>
                  </>
                )}
                <dt>Visit facts</dt>
                <dd className="chg-facts-json">
                  {Object.entries(result.facts)
                    .filter(([, v]) => v != null && v !== "")
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" · ") || "—"}
                </dd>
              </dl>
            </div>
          )}
        </div>
        <div className="chg-modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
